import { Globe } from 'lucide-react';
import { useReportingTimeZone } from '@/hooks/use-reporting-timezone';
import { COMMON_TIME_ZONES, DEFAULT_TIME_ZONE, getLocationTimeZone, formatTimeZoneLabel } from '@/lib/report-timezone';

/**
 * Compact banner shown above the POS reports. Tells the user which timezone
 * every report's date filter and export is anchored to, and lets them override
 * it for their session. The default ("Location default") follows the selected
 * location's configured zone (the app-wide source of truth); an explicit choice
 * here overrides it and persists locally.
 */
export function ReportingTimeZoneBar() {
  const { timeZone, override, setOverride, label } = useReportingTimeZone();

  // Ensure the current override/location zone is always selectable even if it's
  // not one of the common presets.
  const options = [...COMMON_TIME_ZONES];
  if (!options.some((o) => o.value === timeZone)) {
    options.unshift({ value: timeZone, label: formatTimeZoneLabel(timeZone) });
  }

  return (
    <div className="mb-4 flex flex-col gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Globe className="h-4 w-4 shrink-0" />
        <span>
          Reporting timezone: <span className="font-medium text-foreground">{label}</span>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <label htmlFor="reporting-tz" className="text-xs text-muted-foreground">
          Timezone
        </label>
        <select
          id="reporting-tz"
          value={override ?? ''}
          onChange={(e) => setOverride(e.target.value || null)}
          className="h-8 rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="">Location default ({formatTimeZoneLabel(getLocationTimeZone() ?? DEFAULT_TIME_ZONE)})</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
